/**
 * ArkUI.Device - 设备类型检测工具
 * 用于运行时检测设备类型，支持响应式渲染优化
 */

window.ArkUI = window.ArkUI || {};

window.ArkUI.Device = {
    /** @type {DotNetObjectReference} */
    dotNetRef: null,

    /** @type {number} */
    breakpoint: 768,

    /**
     * 矮屏阈值（px）。低于它算「装不下完整桌面 chrome」。
     * 500 的来由：主流手机横屏高 360~412（iPhone 14 Pro 393 / Pixel 7 412）全在线下，
     * 而 iPad 横屏 768、笔记本 768+ 都在线上 —— 后两者空间够，不该被当成紧凑形态。
     * @type {number}
     */
    shortBreakpoint: 500,

    /** @type {boolean} */
    listening: false,

    /** @type {function} */
    resizeHandler: null,

    /** @type {number} */
    debounceTimer: null,

    /**
     * 探测视口并挂上 resize 监听。
     * @param {DotNetObjectReference} dotNetRef - .NET 对象引用
     * @param {number} breakpoint - 宽度断点（默认 768px，与 Tailwind md 一致）
     * @param {number} shortBreakpoint - 高度断点（默认 500px）
     * @returns {{isMobile: boolean, isShort: boolean}}
     *
     * 断点逻辑：
     * - width  < 768 → Mobile（移动版面）
     * - height < 500 → Short（矮屏：手机横屏这类，宽度过了 md 但装不下完整 chrome）
     *
     * ⚠ 两个维度必须一起返回、一起通知。宽度过关不代表版面装得下 ——
     *   手机横屏 852x393 宽度落在桌面侧，高度却只有 393，
     *   这正是「一维断点在横屏设备上系统性失效」那条教训的由来。
     */
    probe(dotNetRef, breakpoint = 768, shortBreakpoint = 500) {
        this.dotNetRef = dotNetRef;
        this.breakpoint = breakpoint;
        this.shortBreakpoint = shortBreakpoint;

        // 设置 resize 监听
        if (!this.listening) {
            this.setupResizeListener();
        }

        return {
            isMobile: window.innerWidth < breakpoint,
            isShort: window.innerHeight < shortBreakpoint
        };
    },

    /**
     * 获取设备类型
     * @returns {number} 0=Desktop, 1=Tablet, 2=Mobile
     */
    getDeviceType() {
        const width = window.innerWidth;
        if (width < 640) return 2;      // Mobile (< sm)
        if (width < 1024) return 1;     // Tablet (sm ~ lg)
        return 0;                        // Desktop (>= lg)
    },

    /**
     * 获取设备信息
     * @returns {object} 设备详细信息
     */
    getInfo() {
        const ua = navigator.userAgent;
        const width = window.innerWidth;
        const height = window.innerHeight;

        return {
            width,
            height,
            pixelRatio: window.devicePixelRatio || 1,
            isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
            isIOS: /iPad|iPhone|iPod/.test(ua),
            isAndroid: /Android/.test(ua),
            isMobile: width < 768,
            isTablet: width >= 768 && width < 1024,
            isDesktop: width >= 1024,
            orientation: width > height ? 'landscape' : 'portrait'
        };
    },

    /**
     * 设置 resize 监听器
     */
    setupResizeListener() {
        if (this.listening) return;

        let lastIsMobile = window.innerWidth < this.breakpoint;
        let lastIsShort = window.innerHeight < this.shortBreakpoint;

        this.resizeHandler = () => {
            // 防抖处理 - 100ms
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }

            this.debounceTimer = setTimeout(() => {
                const isMobile = window.innerWidth < this.breakpoint;
                const isShort = window.innerHeight < this.shortBreakpoint;

                // 只在跨断点时通知（任一维度变了都算）——
                // 转屏会同时改变两者，必须一次回调把两个新值都带过去。
                if (isMobile !== lastIsMobile || isShort !== lastIsShort) {
                    lastIsMobile = isMobile;
                    lastIsShort = isShort;
                    this.notifyChange(isMobile, isShort);
                }
            }, 100);
        };

        window.addEventListener('resize', this.resizeHandler);
        this.listening = true;
    },

    /**
     * 通知 .NET 视口变化
     * @param {boolean} isMobile
     * @param {boolean} isShort
     */
    notifyChange(isMobile, isShort) {
        if (this.dotNetRef) {
            this.dotNetRef.invokeMethodAsync('OnDeviceChanged', isMobile, isShort).catch(() => {
                // 忽略错误（组件可能已销毁）
            });
        }
    },

    /**
     * 清理资源
     */
    cleanup() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }

        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler = null;
        }

        this.dotNetRef = null;
        this.listening = false;
    }
};